import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Date filter for the order pages: Weekly (Mon–Sun), Monthly, Yearly or a Custom range.
// Weekly / Monthly / Yearly load straight away; Custom loads when "Apply" is pressed.
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const pad = (n) => String(n).padStart(2, '0');
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function periodRange(mode, anchor) {
  const y = anchor.getFullYear(), m = anchor.getMonth();
  if (mode === 'week') {
    const start = new Date(y, m, anchor.getDate() - ((anchor.getDay() + 6) % 7));
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    return [ymd(start), ymd(end)];
  }
  if (mode === 'month') return [ymd(new Date(y, m, 1)), ymd(new Date(y, m + 1, 0))];
  return [ymd(new Date(y, 0, 1)), ymd(new Date(y, 11, 31))];
}

function step(mode, anchor, dir) {
  const d = new Date(anchor);
  if (mode === 'week') d.setDate(d.getDate() + 7 * dir);
  else if (mode === 'month') d.setMonth(d.getMonth() + dir, 1);
  else d.setFullYear(d.getFullYear() + dir);
  return d;
}

// full class names written out so Tailwind keeps them in the build
const THEME = {
  blue:   { ring: 'focus:ring-blue-500',   on: 'bg-blue-600 text-white',   btn: 'bg-blue-600 hover:bg-blue-700' },
  orange: { ring: 'focus:ring-orange-500', on: 'bg-orange-600 text-white', btn: 'bg-orange-600 hover:bg-orange-700' },
};

const fmt = (s) => s ? new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

export default function PeriodFilter({ startDate, endDate, setStartDate, setEndDate, onApply, color = 'blue' }) {
  const [mode, setMode] = useState('month');
  const [anchor, setAnchor] = useState(() => new Date());
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: thisYear - 2019 }, (_, i) => thisYear - i);

  const go = (newMode, newAnchor) => {
    setMode(newMode);
    setAnchor(newAnchor);
    if (newMode === 'custom') return;
    const [s, e] = periodRange(newMode, newAnchor);
    onApply(s, e);
  };

  const theme = THEME[color] || THEME.blue;
  const field = `px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${theme.ring} bg-white`;
  const arrow = 'p-2 rounded-lg border border-gray-300 hover:bg-gray-100';

  return (
    <>
      <div>
        <label className="block text-gray-600 text-sm font-medium mb-1">Period</label>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
          {[['week', 'Weekly'], ['month', 'Monthly'], ['year', 'Yearly'], ['custom', 'Custom']].map(([v, label]) => (
            <button key={v} onClick={() => go(v, anchor)}
              className={`px-3 py-2 text-sm font-medium ${mode === v ? theme.on :'bg-white text-gray-700 hover:bg-gray-100'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {mode === 'week' && (
        <div>
          <label className="block text-gray-600 text-sm font-medium mb-1">Week (Mon–Sun) containing</label>
          <div className="flex items-center gap-1">
            <button className={arrow} onClick={() => go('week', step('week', anchor, -1))}><ChevronLeft size={16} /></button>
            <input type="date" className={field} value={ymd(anchor)}
              onChange={(e) => e.target.value && go('week', new Date(e.target.value + 'T00:00:00'))} />
            <button className={arrow} onClick={() => go('week', step('week', anchor, 1))}><ChevronRight size={16} /></button>
          </div>
          <p className="text-xs text-gray-500 mt-1">{fmt(startDate)} – {fmt(endDate)}</p>
        </div>
      )}

      {mode === 'month' && (
        <div>
          <label className="block text-gray-600 text-sm font-medium mb-1">Month</label>
          <div className="flex items-center gap-1">
            <button className={arrow} onClick={() => go('month', step('month', anchor, -1))}><ChevronLeft size={16} /></button>
            <select className={field} value={anchor.getMonth()}
              onChange={(e) => go('month', new Date(anchor.getFullYear(), Number(e.target.value), 1))}>
              {MONTHS.map((name, i) => <option key={name} value={i}>{name}</option>)}
            </select>
            <select className={field} value={anchor.getFullYear()}
              onChange={(e) => go('month', new Date(Number(e.target.value), anchor.getMonth(), 1))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button className={arrow} onClick={() => go('month', step('month', anchor, 1))}><ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {mode === 'year' && (
        <div>
          <label className="block text-gray-600 text-sm font-medium mb-1">Year</label>
          <div className="flex items-center gap-1">
            <button className={arrow} onClick={() => go('year', step('year', anchor, -1))}><ChevronLeft size={16} /></button>
            <select className={field} value={anchor.getFullYear()}
              onChange={(e) => go('year', new Date(Number(e.target.value), 0, 1))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button className={arrow} onClick={() => go('year', step('year', anchor, 1))}><ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {mode === 'custom' && (
        <>
          <div>
            <label className="block text-gray-600 text-sm font-medium mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={field} />
          </div>
          <div>
            <label className="block text-gray-600 text-sm font-medium mb-1">End Date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={field} />
          </div>
          <button onClick={() => onApply(startDate, endDate)}
            className={`px-5 py-2 ${theme.btn} text-white rounded-lg transition-colors font-medium`}>
            Apply
          </button>
        </>
      )}
    </>
  );
}
